import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Layers,
  FileText,
  CheckSquare,
  PlayCircle,
  Circle,
  Check,
  X,
  MinusCircle,
  Loader2,
  ExternalLink,
  AlertCircle,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import type { RunProgress, RunProgressStage, RunStageStatus } from '@mycompbox/shared';
import { cn } from '@/lib/utils';
import { formatDuration, formatEta, secondsBetween } from '@/lib/run-progress-format';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type IconType = React.ComponentType<{ className?: string }>;

/** Icons for the known planning stage keys; unknown keys fall back to Circle. */
const stageIcons: Record<string, IconType> = {
  ANALYZE: Search,
  DECOMPOSE: Layers,
  DETAIL: FileText,
  EVALUATE: CheckSquare,
  VERIFY: PlayCircle,
};

/** Text label for each stage status, so status never relies on color alone. */
const statusLabels: Record<RunStageStatus, string> = {
  pending: 'Waiting',
  active: 'In progress',
  completed: 'Done',
  failed: 'Failed',
  skipped: 'Skipped',
};

/** How often the panel re-renders elapsed and "no update" times. */
const TICK_MS = 1000;

// ---------------------------------------------------------------------------
// Hooks & helpers
// ---------------------------------------------------------------------------

/** Current time, refreshed every TICK_MS while `live` is true. */
function useNow(live: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    setNow(Date.now());
    const interval = setInterval(() => {
      setNow(Date.now());
    }, TICK_MS);
    return () => {
      clearInterval(interval);
    };
  }, [live]);
  return now;
}

/** 1-based number of the stage the run is on, for "Stage N of M". */
function currentStageNumber(progress: RunProgress): number {
  const total = progress.stages.length;
  if (progress.currentStageKey) {
    const idx = progress.stages.findIndex((s) => s.key === progress.currentStageKey);
    if (idx >= 0) return idx + 1;
  }
  const firstOpen = progress.stages.findIndex(
    (s) => s.status === 'active' || s.status === 'pending' || s.status === 'failed',
  );
  if (firstOpen >= 0) return firstOpen + 1;
  return total;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StageStatusIcon({ stage }: { stage: RunProgressStage }) {
  switch (stage.status) {
    case 'completed':
      return <Check className="h-4 w-4 text-green-400" aria-hidden />;
    case 'active':
      return <Loader2 className="h-4 w-4 text-purple-400 animate-spin" aria-hidden />;
    case 'failed':
      return <X className="h-4 w-4 text-red-400" aria-hidden />;
    case 'skipped':
      return <MinusCircle className="h-4 w-4 text-muted-foreground/50" aria-hidden />;
    default: {
      const Icon = stageIcons[stage.key] ?? Circle;
      return <Icon className="h-4 w-4 text-muted-foreground/50" aria-hidden />;
    }
  }
}

function StageRow({ stage, now }: { stage: RunProgressStage; now: number }) {
  const elapsed =
    stage.startedAt && (stage.status === 'active' || stage.completedAt)
      ? secondsBetween(stage.startedAt, stage.completedAt, now)
      : null;

  return (
    <li
      data-testid={`stage-${stage.key}`}
      data-status={stage.status}
      aria-current={stage.status === 'active' ? 'step' : undefined}
      className={cn(
        'flex items-start gap-3 rounded-lg px-3 py-2 border',
        stage.status === 'active' && 'border-purple-500/40 bg-purple-500/10',
        stage.status === 'completed' && 'border-green-500/20 bg-green-900/10',
        stage.status === 'failed' && 'border-red-500/40 bg-red-500/10',
        (stage.status === 'pending' || stage.status === 'skipped') && 'border-muted-foreground/10',
      )}
    >
      <div className="mt-0.5 shrink-0">
        <StageStatusIcon stage={stage} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-sm font-medium',
              stage.status === 'active' && 'text-purple-300',
              stage.status === 'completed' && 'text-foreground',
              stage.status === 'failed' && 'text-red-300',
              (stage.status === 'pending' || stage.status === 'skipped') && 'text-muted-foreground',
            )}
          >
            {stage.label}
          </span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {statusLabels[stage.status]}
          </span>
          {elapsed !== null && (
            <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
              {formatDuration(elapsed)}
            </span>
          )}
        </div>
        {stage.detail && (stage.status === 'active' || stage.status === 'failed') && (
          <p className="text-xs text-muted-foreground mt-0.5 break-words">{stage.detail}</p>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface PlanningProgressPanelProps {
  /** Live progress of the planning run. */
  progress: RunProgress;
  /** Epic created by the run, linked once the run completes. */
  convertedEpicId?: string | null | undefined;
}

export function PlanningProgressPanel({ progress, convertedEpicId }: PlanningProgressPanelProps) {
  const isLive = progress.state === 'active' || progress.state === 'stalled' || progress.state === 'pending';
  const now = useNow(isLive);

  const totalStages = progress.stages.length;
  const stageNumber = currentStageNumber(progress);
  const progressPct =
    progress.total > 0 ? Math.round((Math.min(progress.completed, progress.total) / progress.total) * 100) : 0;
  const failedStage = progress.stages.find((s) => s.status === 'failed') ?? null;

  let header: string;
  switch (progress.state) {
    case 'completed':
      header = `All ${String(totalStages)} stages done`;
      break;
    case 'failed':
      header = failedStage ? `Failed at stage ${String(stageNumber)} of ${String(totalStages)}` : 'Planning failed';
      break;
    case 'cancelled':
      header = 'Planning was cancelled';
      break;
    case 'pending':
      header = 'Waiting for planning to start';
      break;
    default:
      header = `Stage ${String(stageNumber)} of ${String(totalStages)}`;
  }

  const showEta = progress.state === 'active' || progress.state === 'stalled';
  const totalSeconds = secondsBetween(progress.startedAt, isLive ? null : progress.updatedAt, now);
  const minutesSinceUpdate = Math.floor(secondsBetween(progress.updatedAt, null, now) / 60);

  return (
    <div className="space-y-3" data-testid="planning-progress-panel" data-state={progress.state}>
      {/* Header: stage position and ETA */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-sm font-semibold">{header}</h3>
        {showEta && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="planning-eta">
            <Clock className="h-3 w-3" aria-hidden />
            {formatEta(progress.eta)}
          </span>
        )}
        {progress.state === 'completed' && (
          <span className="text-xs text-muted-foreground" data-testid="planning-duration">
            Took {formatDuration(totalSeconds)}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div
        className="h-1 w-full rounded-full bg-muted/30 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progressPct}
        aria-label="Planning progress"
      >
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            progress.state === 'failed' ? 'bg-red-500/70' : 'bg-gradient-to-r from-purple-500 to-green-500',
          )}
          style={{ width: `${String(progressPct)}%` }}
        />
      </div>

      {/* Stalled warning */}
      {progress.state === 'stalled' && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-lg border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-xs text-yellow-300"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          <span>
            No update in {minutesSinceUpdate}m.
            {progress.failureReason ? ` ${progress.failureReason}` : ' The planner may be stuck.'}
          </span>
        </div>
      )}

      {/* Failure */}
      {progress.state === 'failed' && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300"
        >
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
          <span className="break-words">
            {failedStage ? `${failedStage.label} failed. ` : ''}
            {progress.failureReason ?? 'No reason was reported.'}
          </span>
        </div>
      )}

      {/* Stages */}
      <ol className="space-y-1.5" aria-label="Planning stages">
        {progress.stages.map((stage) => (
          <StageRow key={stage.key} stage={stage} now={now} />
        ))}
      </ol>

      {/* Completed: link to the created epic */}
      {progress.state === 'completed' && convertedEpicId && (
        <Link
          to={`/epics/${convertedEpicId}`}
          className="inline-flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300"
        >
          <ExternalLink className="h-3 w-3" aria-hidden />
          View epic
        </Link>
      )}
    </div>
  );
}
